// create a user slice in src/store/user.slice.ts

import { createSlice } from '@reduxjs/toolkit';
import { type Role } from 'components/Permission/permission.types';
import { type DriverBehaviorThresholds } from 'types/customer.types';
import { type User } from 'types/user.types';

export interface UserState {
  role: null | Role;
  email: string;
  name: string;
  userId: string;
  isAuthenticated: boolean;
  accessToken: string;
  isUserInfoLoading: boolean;
  userInfo: User | undefined;
  customer: {
    id: string;
    name: string;
    code: string;
    email: string;
    contactFirstName: string;
    contactLastName: string;
    phoneNumber: string;
    internalAccountingLink: string;
    unitSystem: string;
    theme: string;
    timezone: string;
    mineType: string;
    isTiresModule: boolean;
    proj4String: string | null;
    localOriginEasting: number | null;
    localOriginNorthing: number | null;
    driverBehavior: DriverBehaviorThresholds;
    modules?: {
      isPartsModule: boolean;
      isTiresModule: boolean;
      isMachinesModule: boolean;
    };
  };
}

const initialState: UserState = {
  role: null,
  email: '',
  name: '',
  userId: '',
  isAuthenticated: false,
  accessToken: '',
  userInfo: undefined,
  isUserInfoLoading: true,
  customer: {
    id: '',
    name: '',
    code: '',
    email: '',
    phoneNumber: '',
    contactFirstName: '',
    contactLastName: '',
    internalAccountingLink: '',
    unitSystem: '',
    theme: 'light',
    timezone: '',
    mineType: '',
    isTiresModule: false,
    proj4String: null,
    localOriginEasting: null,
    localOriginNorthing: null,
    driverBehavior: {
      lowThreshold: 0,
      highThreshold: 0,
    },
    modules: undefined,
  },
};

// creaet a user slice
export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // add reducers here
    updateAuthState(state, { payload } /* : { payload: UserState } */) {
      return {
        ...state,
        ...payload,
      };
    },
    updateCustomer(
      state,
      { payload }: { payload: Partial<UserState['customer']> }
    ) {
      // merge so user-level fields (e.g. theme) are not clobbered by customer sync
      return {
        ...state,
        customer: {
          ...state.customer,
          ...payload,
        },
      };
    },
    resetUserState() {
      return initialState;
    },
    //   extraReducers(builder) {},
    updateCustomerTheme(state, { payload }) {
      state.customer.theme = payload.theme;
      return state;
    },
    //
    updateUserInfo(state, { payload }: { payload: User }) {
      return {
        ...state,
        userInfo: payload,
      };
    },

    updateUserInfoLoading(state, { payload }: { payload: boolean }) {
      return {
        ...state,
        isUserInfoLoading: payload,
      };
    },
  },
});

export const {
  updateAuthState,
  updateCustomer,
  resetUserState,
  updateCustomerTheme,
  updateUserInfo,
  updateUserInfoLoading,
} = userSlice.actions;

export default userSlice.reducer;
